// Sessão do console. Dois modos, com a mesma interface para as telas:
// - cognito: login real por código no e-mail (cognito.ts), ligado quando o build recebe
//   VITE_COGNITO_REGION e VITE_COGNITO_CLIENT_ID (deploy-iuvo-console.yml lê do contrato SSM);
// - demo: login simulado pelo mock da API (código 246810), quando o build não tem Cognito.
//
// Enquanto a camada /console/v1 real não existe (#4391), o modo cognito convive com o mock dos
// dados: o login é de verdade e o mock mapeia a pessoa para uma persona fictícia (cabeçalho
// X-Demo-Persona, só com VITE_MOCKS=on; o backend real ignora).
import { api, unwrap, ApiError } from "@/api/client";
import type { Me, Role, Session } from "@/api/types";
import { createCognitoAuth, loginErrorMessage, type CognitoAuth, type RefreshStore } from "./cognito";

const TOKEN_KEY = "iuvo-console-token";
const REMEMBER_KEY = "iuvo-console-remember";
const REFRESH_KEY = "iuvo-console-refresh";
const PERSONA_KEY = "iuvo-console-demo-persona";

function read(key: string, storage: () => Storage): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null, storage: () => Storage) {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    /* modo privado / storage bloqueado: sessão fica só em memória */
  }
}

const region = import.meta.env.VITE_COGNITO_REGION as string | undefined;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
export const AUTH_MODE: "cognito" | "demo" = region && clientId ? "cognito" : "demo";
export const isDemoAuth = AUTH_MODE === "demo";
const MOCKS_ON = import.meta.env.VITE_MOCKS === "on";

// Refresh token: sessionStorage (morre ao fechar a aba) ou, com "Manter conectado", localStorage.
// O access token fica só em memória.
let memoryRefresh: string | null = null;
const refreshStore: RefreshStore = {
  get: () => read(REFRESH_KEY, () => sessionStorage) ?? read(REFRESH_KEY, () => localStorage) ?? memoryRefresh,
  set: (value, remember) => {
    memoryRefresh = value;
    write(REFRESH_KEY, value, () => sessionStorage);
    write(REFRESH_KEY, value !== null && remember ? value : null, () => localStorage);
  },
  remembered: () => read(REFRESH_KEY, () => localStorage) !== null,
};

const cognito: CognitoAuth | null = AUTH_MODE === "cognito" ? createCognitoAuth({ region: region!, clientId: clientId! }, refreshStore) : null;

// ---- modo demo ----
let memoryToken: string | null = null;
const demoToken = () => read(TOKEN_KEY, () => sessionStorage) ?? read(REMEMBER_KEY, () => localStorage) ?? memoryToken;
function keepDemo(session: Session, remember: boolean) {
  memoryToken = session.token;
  write(TOKEN_KEY, session.token, () => sessionStorage);
  write(REMEMBER_KEY, remember ? session.token : null, () => localStorage);
}

/** Token para a próxima chamada da API (renova o do Cognito antes de vencer). */
export async function getToken(): Promise<string | null> {
  return cognito ? cognito.accessToken() : demoToken();
}

/** Há uma sessão para tentar restaurar no boot (sem rede). */
export function hasSession(): boolean {
  return cognito ? cognito.hasSession() : demoToken() !== null;
}

/** Depois de um 401: renova uma vez. `null` = a sessão acabou de verdade. */
export async function forceRenew(): Promise<string | null> {
  return cognito ? cognito.forceRenew() : null;
}

/** Persona fictícia escolhida em "Ver como", para o mock (só com VITE_MOCKS=on e Cognito). */
export function demoPersona(): string | null {
  return cognito && MOCKS_ON ? read(PERSONA_KEY, () => sessionStorage) : null;
}

/** Limpa só o local (logout forçado por 401). */
export function clearSession() {
  memoryToken = null;
  write(TOKEN_KEY, null, () => sessionStorage);
  write(REMEMBER_KEY, null, () => localStorage);
  write(PERSONA_KEY, null, () => sessionStorage);
  cognito?.clear();
}

/** Sair: revoga o refresh no Cognito e limpa local. */
export async function logout(): Promise<void> {
  const c = cognito;
  write(PERSONA_KEY, null, () => sessionStorage);
  if (c) await c.logout();
  clearSession();
}

export interface Challenge {
  challengeId: string;
  demoCode?: string;
}

export async function startEmailLogin(email: string): Promise<Challenge> {
  if (cognito) return { challengeId: await cognito.start(email) };
  return unwrap(api.POST("/auth/email/start", { body: { email } }));
}

export async function verifyEmailLogin(challenge: { id: string; email: string }, code: string, remember: boolean): Promise<Me> {
  if (cognito) {
    await cognito.verify(challenge.email, challenge.id, code, remember);
    try {
      return await unwrap(api.GET("/me"));
    } catch (e) {
      clearSession();
      throw meLoadError(e);
    }
  }
  const s = await unwrap(api.POST("/auth/email/verify", { body: { challengeId: challenge.id, code } }));
  keepDemo(s, remember);
  return s.me;
}

/** Texto do erro de login para a pessoa. */
export function loginError(e: unknown): string {
  if (e instanceof ApiError) return e.message.replace(/: /, " ");
  return loginErrorMessage(e);
}

export async function demoLogin(): Promise<Me> {
  const s = await unwrap(api.POST("/auth/demo"));
  keepDemo(s, false);
  return s.me;
}

/** "Ver como" da demonstração: troca o personagem fictício (só existe com o mock). */
export async function switchRole(role: Role): Promise<Me> {
  const s = await unwrap(api.POST("/auth/switch", { body: { role } }));
  if (cognito) write(PERSONA_KEY, s.me.id, () => sessionStorage);
  else keepDemo(s, read(REMEMBER_KEY, () => localStorage) !== null);
  return s.me;
}

/**
 * Erro para a pessoa quando o código foi aceito mas o perfil (`/me`) não veio. Só 401/403 é
 * "acesso não liberado"; o resto (resposta que não é JSON, rede, 5xx) é falha de carga, e dizer
 * "não liberado" nesse caso manda a pessoa atrás do administrador à toa.
 */
export function meLoadError(e: unknown): ApiError {
  console.warn("[iuvo-console] /me falhou depois do login:", e);
  if (e instanceof ApiError && (e.isAuth || e.isForbidden)) {
    return new ApiError(e.status, "Seu acesso ainda não foi liberado.", "Fale com o administrador da sua instituição.");
  }
  return new ApiError(0, "Não foi possível carregar seu perfil.", "Recarregue a página e entre de novo.");
}

export async function fetchMe(): Promise<Me | null> {
  if (!hasSession()) return null;
  try {
    return await unwrap(api.GET("/me"));
  } catch (e) {
    console.warn("[iuvo-console] /me falhou ao restaurar a sessão:", e);
    clearSession();
    return null;
  }
}
